/* ==================================================================
   IVYN CARD MEDIA — intent-gated image loading + swipe hint  (rev 7)
   ------------------------------------------------------------------
   HISTORY — read before editing.

   rev 1: deferred extra card slides, then shipped two blanket fallbacks
          (2500ms IntersectionObserver, 6000ms window.load sweep) that
          promoted every image with no user intent, cancelling the fix.
          Measured after rev 1: /collections/formal-pants LCP 16.9s,
          payload 11.0MB.
   rev 2: both fallbacks REMOVED. Extra slides promote only on intent.
   rev 3: .img-hover deferred in templates, promoted here by pointer
          capability, so phones stop downloading an image hover can
          never display.
   rev 4: single-execution guard. The include used to sit inside
          snippets/product-card.liquid and {% render %} scopes each call
          separately, so it emitted one script tag per card.
   rev 5: mobile swipe hint (this file only — no template changes).
   rev 6: live visibility re-check at dequeue time. The observer ratio is
          only a snapshot from queue time; a queued card can be scrolled
          past before its turn. Such a card is NOT marked done — it goes
          back to the observer.
   rev 7: second live visibility re-check at the END of the dwell, right
          before the motion starts, with the same not-done / re-observe
          handling. The single next-slide promotion now happens AFTER
          that check, so a card the shopper scrolled past during the
          dwell costs ZERO image requests.

   ---- SWIPE HINT DESIGN NOTES -------------------------------------
   theme.js owns the card slider. Its per-card state lives in a private
   WeakMap; get/set are NOT exported. Only window.ivynCardNav and
   window.ivynCardReset are reachable. The hint therefore NEVER touches
   the slide index — it is purely a visual nudge applied with CSS
   classes on .pc-track, and theme.js keeps ownership of the real
   transform at all times.

   Because theme.js writes transform as an INLINE style during drags,
   the hint classes use !important so a class can drive the track while
   no inline transform exists (fresh load). The moment the shopper
   interacts, the hint is cancelled with transition:none and the classes
   are stripped, so theme.js's inline transform wins from frame one and
   the drag follows the finger with no fight and no stale transform.

   Image cost: at most ONE extra request per hinted card — the second
   slide only, and only once the card has passed BOTH visibility checks.
   data-mediaWarmed is deliberately NOT set, so slides 3+ stay
   intent-gated and a later real touch still warms them normally.
   ================================================================== */
(function () {
  "use strict";

  /* Single-execution guard (rev 4). If this file is ever included more
     than once, the browser caches the download but would otherwise run
     the module again and register every listener twice. */
  if (window.__ivynCardMediaInit) return;
  window.__ivynCardMediaInit = true;

  var mq = window.matchMedia ? function (q) { return window.matchMedia(q).matches; } : function () { return false; };

  var HOVER_CAPABLE = mq("(hover: hover) and (pointer: fine)");
  var COARSE        = mq("(hover: none) and (pointer: coarse)");
  var REDUCED       = mq("(prefers-reduced-motion: reduce)");

  /* ================================================================
     PART 1 — deferred image promotion (rev 2/3 behaviour, unchanged)
     ================================================================ */

  function promote(img) {
    if (!img) return;
    var ds = img.getAttribute("data-src");
    if (!ds) return;
    var dss = img.getAttribute("data-srcset");
    if (dss) img.setAttribute("srcset", dss);
    img.setAttribute("src", ds);
    img.removeAttribute("data-src");
    img.removeAttribute("data-srcset");
  }

  /* Promotes EVERY deferred image in one card. Only ever called from a
     genuine intent signal. The swipe hint does NOT call this. */
  function warmCard(pi) {
    if (!pi || pi.dataset.mediaWarmed) return;
    pi.dataset.mediaWarmed = "1";
    var imgs = pi.querySelectorAll("img[data-src]");
    for (var i = 0; i < imgs.length; i++) promote(imgs[i]);
  }

  function fromEvent(e) {
    var t = e.target;
    if (!t || !t.closest) return null;
    return t.closest(".pi");
  }

  var OPTS = { passive: true, capture: true };

  document.addEventListener("pointerenter", function (e) {
    if (e.pointerType === "touch") return;
    var pi = fromEvent(e);
    if (pi) warmCard(pi);
  }, OPTS);

  document.addEventListener("touchstart", function (e) {
    var pi = fromEvent(e);
    if (pi) { cancelHint(pi); warmCard(pi); }
  }, OPTS);

  document.addEventListener("pointerdown", function (e) {
    var pi = fromEvent(e);
    if (pi) { cancelHint(pi); warmCard(pi); }
  }, OPTS);

  document.addEventListener("focusin", function (e) {
    var pi = fromEvent(e);
    if (pi) warmCard(pi);
  }, OPTS);

  /* Colour swatches: theme.js assigns .img-hover.src from data-hover-src.
     This listener is on capture, so the card warms BEFORE theme.js runs
     and data-src cannot later overwrite the swatch-selected image. */
  document.addEventListener("click", function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var wrapAny = t.closest(".pc-wrap");
    if (wrapAny) cancelHint(wrapAny.querySelector(".pi"));
    var sw = t.closest(".pswatch");
    if (!sw) return;
    var wrap = sw.closest(".pc-wrap");
    if (wrap) warmCard(wrap.querySelector(".pi"));
  }, OPTS);

  /* Arrow buttons call window.ivynCardNav (theme.js). Wrap it so the
     card is warmed and any running hint is cancelled BEFORE theme.js
     measures slide count and moves the track — otherwise the first
     arrow click lands on a blank slide or fights the hint transform. */
  function wrapNav() {
    var orig = window.ivynCardNav;
    if (typeof orig !== "function" || orig.__ivynWrapped) return;
    var wrapped = function (btn, dir) {
      try {
        var wrap = btn && btn.closest ? btn.closest(".pc-wrap") : null;
        if (wrap) {
          var pi = wrap.querySelector(".pi");
          cancelHint(pi);
          warmCard(pi);
        }
      } catch (err) {}
      return orig.apply(this, arguments);
    };
    wrapped.__ivynWrapped = true;
    window.ivynCardNav = wrapped;
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", wrapNav);
  } else {
    wrapNav();
  }
  window.addEventListener("load", wrapNav);

  /* ---- Desktop-only hover-image sweep --------------------------------
     NOT the rev 1 bug returning: scoped to .img-hover only, runs only on
     a genuine hover-capable fine pointer, fires at idle after load so it
     cannot compete with LCP, and does NOT set data-mediaWarmed — extra
     slides stay deferred. Extra slides have NO timer and NO visibility
     trigger. Do not add one. */
  function promoteHoverImages() {
    if (!HOVER_CAPABLE) return;
    var imgs = document.querySelectorAll("img.img-hover[data-src]");
    for (var i = 0; i < imgs.length; i++) promote(imgs[i]);
  }

  function afterLoad() {
    if (!HOVER_CAPABLE) return;
    if (window.requestIdleCallback) {
      window.requestIdleCallback(promoteHoverImages, { timeout: 1500 });
    } else {
      setTimeout(promoteHoverImages, 200);
    }
  }

  if (document.readyState === "complete") {
    afterLoad();
  } else {
    window.addEventListener("load", afterLoad);
  }

  /* ================================================================
     PART 2 — mobile swipe hint
     ================================================================ */

  var HINT_OK = COARSE && !REDUCED && !HOVER_CAPABLE && !!window.IntersectionObserver;

  var DWELL     = 600;   /* visible-for before the nudge starts        */
  var OUT_MS    = 380;   /* travel out                                 */
  var HOLD_MS   = 160;   /* pause at the revealed position             */
  var BACK_MS   = 420;   /* travel home  (total motion ~960ms)         */
  var GAP_MS    = 420;   /* stagger between cards — never two at once  */
  var MIN_RATIO = 0.6;   /* visible fraction required at every gate    */
  var READY_CAP = 400;   /* max wait for slide 2 to decode             */

  var hintTimers = null;   /* WeakMap pi -> [timeoutIds]               */
  var queue      = [];
  var busy       = false;
  var disabled   = false;  /* set on bfcache restore                   */

  if (window.WeakMap) hintTimers = new WeakMap();

  function getTimers(pi) {
    if (!hintTimers) return null;
    var t = hintTimers.get(pi);
    if (!t) { t = []; hintTimers.set(pi, t); }
    return t;
  }

  function later(pi, fn, ms) {
    var id = setTimeout(fn, ms);
    var t = getTimers(pi);
    if (t) t.push(id);
    return id;
  }

  function clearTimers(pi) {
    if (!hintTimers) return;
    var t = hintTimers.get(pi);
    if (!t) return;
    for (var i = 0; i < t.length; i++) clearTimeout(t[i]);
    hintTimers.set(pi, []);
  }

  /* Live visible fraction of the card, measured NOW. The observer's
     ratio is only a snapshot from queue time, and the dwell adds another
     600ms in which the shopper can scroll well past the card. Animating
     an off-screen card would burn its one-shot hint on something nobody
     saw — and, before rev 7, would also have spent an image request. */
  function visibleRatio(pi) {
    if (!pi || !pi.getBoundingClientRect) return 0;
    var r = pi.getBoundingClientRect();
    if (!r.width || !r.height) return 0;
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    var vw = window.innerWidth || document.documentElement.clientWidth || 0;
    var y = Math.min(r.bottom, vh) - Math.max(r.top, 0);
    var x = Math.min(r.right, vw) - Math.max(r.left, 0);
    if (y <= 0 || x <= 0) return 0;
    return (y * x) / (r.width * r.height);
  }

  /* Hand a card back without consuming its one-shot hint. Used whenever
     a visibility gate fails: no done flag, no timers left running, the
     queue slot is released and the observer takes the card again. */
  function deferCard(pi) {
    if (!pi || !pi.dataset) return;
    delete pi.dataset.swipeHintActive;
    clearTimers(pi);
    if (io) io.observe(pi);
    releaseIfCurrent(pi);
  }

  /* Strips the hint instantly, with transition suppressed, so theme.js
     inherits a track sitting exactly on slide 1 and its drag maths are
     correct from the first frame. Safe to call on any element, any time,
     including cards that were never hinted. */
  function cancelHint(pi) {
    if (!pi || !pi.dataset) return;
    var track = pi.querySelector(".pc-track");
    clearTimers(pi);
    pi.dataset.swipeHintDone = "1";
    delete pi.dataset.swipeHintActive;
    if (!track) return;
    if (track.classList.contains("ivyn-hint-out") || track.classList.contains("ivyn-hint-back")) {
      track.classList.add("ivyn-hint-cancel");
      track.classList.remove("ivyn-hint-out", "ivyn-hint-back");
      /* forced reflow commits the class removal before transition is
         re-enabled, so there is no animated snap-back */
      void track.offsetWidth;
      track.classList.remove("ivyn-hint-cancel");
    }
    releaseIfCurrent(pi);
  }

  var current = null;
  function releaseIfCurrent(pi) {
    if (current !== pi) return;
    current = null;
    busy = false;
    setTimeout(pump, GAP_MS);
  }

  function injectCss() {
    if (document.getElementById("ivyn-hint-css")) return;
    var css =
      ".pc-track.ivyn-hint-out{transform:translate3d(-13%,0,0)!important;" +
      "transition:transform " + OUT_MS + "ms cubic-bezier(.32,.72,.28,1)!important}" +
      ".pc-track.ivyn-hint-back{transform:translate3d(0,0,0)!important;" +
      "transition:transform " + BACK_MS + "ms cubic-bezier(.32,.72,.28,1)!important}" +
      ".pc-track.ivyn-hint-cancel{transition:none!important}";
    var el = document.createElement("style");
    el.id = "ivyn-hint-css";
    el.appendChild(document.createTextNode(css));
    (document.head || document.documentElement).appendChild(el);
  }

  function eligible(pi) {
    if (!HINT_OK || disabled) return false;
    if (!pi || !pi.dataset) return false;
    if (pi.dataset.swipeHintDone || pi.dataset.swipeHintActive) return false;
    if (pi.dataset.mediaWarmed) return false;   /* already interacted with */
    var track = pi.querySelector(".pc-track");
    if (!track) return false;
    if (track.querySelectorAll("img").length < 2) return false;
    return true;
  }

  /* Slide 2 is requested at the last possible moment, so it may still be
     in flight when the nudge is due. Revealing 13% of a blank frame is
     worse than a slightly later nudge, so wait for decode — capped, and
     the wait is a tracked timer so cancellation still kills it. */
  function whenReady(pi, img, cb) {
    if (!img) { cb(); return; }
    if (img.complete && img.naturalWidth > 0) { cb(); return; }
    var fired = false;
    function go() {
      if (fired) return;
      fired = true;
      cb();
    }
    img.addEventListener("load", go);
    img.addEventListener("error", go);
    later(pi, go, READY_CAP);
  }

  function runHint(pi) {
    if (!eligible(pi)) { releaseIfCurrent(pi); return; }
    var track = pi.querySelector(".pc-track");

    pi.dataset.swipeHintActive = "1";

    later(pi, function () {
      if (!pi.dataset.swipeHintActive) return;

      /* Final gate. 600ms is long enough for the shopper to have scrolled
         the card away; if so, give it back rather than spending either
         the one-shot hint or an image request on it. */
      if (visibleRatio(pi) < MIN_RATIO) { deferCard(pi); return; }

      /* Promote ONLY the next slide, and only now. Deliberately not
         warmCard() — that would set data-mediaWarmed and pull every
         remaining slide. */
      var next = track.querySelector("img[data-src]");
      if (next) promote(next);

      whenReady(pi, next, function () {
        if (!pi.dataset.swipeHintActive) return;
        track.classList.add("ivyn-hint-out");

        later(pi, function () {
          if (!pi.dataset.swipeHintActive) return;
          track.classList.remove("ivyn-hint-out");
          track.classList.add("ivyn-hint-back");

          later(pi, function () {
            track.classList.remove("ivyn-hint-back");
            pi.dataset.swipeHintDone = "1";
            delete pi.dataset.swipeHintActive;
            releaseIfCurrent(pi);
          }, BACK_MS);
        }, OUT_MS + HOLD_MS);
      });
    }, DWELL);
  }

  function pump() {
    if (busy || disabled) return;
    var pi = null;
    while (queue.length) {
      var cand = queue.shift();
      if (!eligible(cand) || cand.isConnected === false) continue;
      /* First gate — live, not the queue-time snapshot. A card that has
         scrolled away is NOT marked done; it goes back to the observer. */
      if (visibleRatio(cand) < MIN_RATIO) {
        if (io) io.observe(cand);
        continue;
      }
      pi = cand;
      break;
    }
    if (!pi) return;
    busy = true;
    current = pi;
    injectCss();
    runHint(pi);
  }

  var io = null;
  function observeCards() {
    if (!HINT_OK || disabled) return;
    if (!io) {
      io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var en = entries[i];
          var pi = en.target;
          if (en.isIntersecting && en.intersectionRatio >= MIN_RATIO) {
            if (eligible(pi) && queue.indexOf(pi) === -1) {
              queue.push(pi);
              io.unobserve(pi);   /* never re-queue on scroll up/down */
              pump();
            }
          }
        }
      }, { threshold: [0, MIN_RATIO] });
    }
    var cards = document.querySelectorAll(".pc-wrap .pi");
    for (var i = 0; i < cards.length; i++) {
      var pi = cards[i];
      if (pi.dataset.hintObserved) continue;
      pi.dataset.hintObserved = "1";
      if (eligible(pi)) io.observe(pi);
    }
  }

  if (HINT_OK) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", observeCards);
    } else {
      observeCards();
    }
    window.addEventListener("load", observeCards);

    /* cards injected by filters or pagination */
    if (window.MutationObserver) {
      var pending = false;
      new MutationObserver(function () {
        if (pending || disabled) return;
        pending = true;
        requestAnimationFrame(function () { pending = false; observeCards(); });
      }).observe(document.body || document.documentElement, { childList: true, subtree: true });
    }
  }

  /* Back-navigation with a restored page: the DOM comes back exactly as
     it was, so replaying the hint would be a second unexplained motion.
     Disable hinting entirely for the rest of that page's life. */
  window.addEventListener("pageshow", function (e) {
    if (!e.persisted) return;
    disabled = true;
    queue.length = 0;
    if (current) cancelHint(current);
  });
})();
